import React, { useState } from 'react';
import { Settings, Users, Shield, Plus, Edit, Trash2, Mail, Eye, X, Check, ChevronDown, User, Radio, UserPlus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { AdminLayout } from '@/components/layout/AdminLayout';
import { toast } from 'sonner';

// Role definitions with permissions
const ROLES = {
  viewer: {
    id: 'viewer',
    name: 'Viewer',
    description: 'Can view assigned events and data. No editing permissions.',
    color: 'bg-gray-500',
    permissions: ['view_assigned_events', 'view_analytics']
  },
  editor: {
    id: 'editor',
    name: 'Editor',
    description: 'Can view and edit assigned events. Cannot delete or manage users.',
    color: 'bg-blue-500',
    permissions: ['view_assigned_events', 'edit_assigned_events', 'view_analytics', 'manage_content']
  },
  artist: {
    id: 'artist',
    name: 'Artist',
    description: 'Can view and manage their own artist profile and assigned events.',
    color: 'bg-purple-500',
    permissions: ['view_assigned_events', 'edit_artist_profile', 'view_analytics']
  },
  stream_engineer: {
    id: 'stream_engineer',
    name: 'Stream Engineer',
    description: 'Can manage streaming configuration for assigned events.',
    color: 'bg-cyan-500',
    permissions: ['view_assigned_events', 'manage_stream_config', 'view_stream_monitor']
  },
  administrator: {
    id: 'administrator',
    name: 'Administrator',
    description: 'Full access to all events and settings. Can manage users.',
    color: 'bg-orange-500',
    permissions: ['full_event_access', 'manage_users', 'manage_settings', 'view_analytics', 'manage_content']
  },
  super_administrator: {
    id: 'super_administrator',
    name: 'Super Administrator',
    description: 'Complete platform access including billing, integrations, and all admin functions.',
    color: 'bg-red-500',
    permissions: ['full_platform_access', 'manage_billing', 'manage_integrations', 'manage_all_users', 'delete_events']
  }
};

// Mock team members data
const initialTeamMembers = [
  { id: 'tm-001', name: 'John Admin', email: 'john@exit88.com', role: 'super_administrator', status: 'active', assignedEvents: [] },
  { id: 'tm-002', name: 'Sarah Manager', email: 'sarah@exit88.com', role: 'administrator', status: 'active', assignedEvents: [] },
  { id: 'tm-003', name: 'Mike Editor', email: 'mike@exit88.com', role: 'editor', status: 'active', assignedEvents: ['evt-001', 'evt-002'] },
  { id: 'tm-004', name: 'DJ Midnight', email: 'midnight@artist.com', role: 'artist', status: 'active', assignedEvents: ['evt-001'] },
  { id: 'tm-005', name: 'Alex Stream', email: 'alex@exit88.com', role: 'stream_engineer', status: 'active', assignedEvents: ['evt-001', 'evt-003'] },
  { id: 'tm-006', name: 'View Only', email: 'viewer@exit88.com', role: 'viewer', status: 'pending', assignedEvents: ['evt-002'] },
];

// Invite User Modal
const InviteUserModal = ({ isOpen, onClose, onInvite }) => {
  const [formData, setFormData] = useState({
    email: '',
    name: '',
    role: 'viewer'
  });

  if (!isOpen) return null;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.email || !formData.name) {
      toast.error('Please fill in all required fields');
      return;
    }
    onInvite(formData);
    setFormData({ email: '', name: '', role: 'viewer' });
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="relative bg-card border border-border rounded-xl w-full max-w-md p-6 animate-in zoom-in-95">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-white">Invite Team Member</h2>
          <button onClick={onClose} className="p-2 hover:bg-secondary rounded-lg">
            <X className="w-5 h-5 text-muted-foreground" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <label className="text-sm text-muted-foreground">Full Name *</label>
            <Input
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="Enter full name"
              className="bg-secondary border-border"
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm text-muted-foreground">Email Address *</label>
            <Input
              type="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              placeholder="email@example.com"
              className="bg-secondary border-border"
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm text-muted-foreground">Role *</label>
            <select
              value={formData.role}
              onChange={(e) => setFormData({ ...formData, role: e.target.value })}
              className="w-full h-10 px-3 rounded-md bg-secondary border border-border text-white"
            >
              {Object.values(ROLES).map(role => (
                <option key={role.id} value={role.id}>{role.name}</option>
              ))}
            </select>
            <p className="text-xs text-muted-foreground mt-1">
              {ROLES[formData.role]?.description}
            </p>
          </div>

          <div className="flex gap-3 pt-4">
            <Button type="button" variant="outline" className="flex-1" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" className="flex-1 bg-primary text-primary-foreground">
              <Mail className="w-4 h-4 mr-2" />
              Send Invite
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Role Badge Component
const RoleBadge = ({ role }) => {
  const roleData = ROLES[role];
  if (!roleData) return null;

  return (
    <span className={`px-2 py-1 rounded-full text-xs font-medium ${roleData.color}/20 text-white`}>
      <span className={`inline-block w-2 h-2 rounded-full ${roleData.color} mr-1.5`} />
      {roleData.name}
    </span>
  );
};

// Team Member Row
const TeamMemberRow = ({ member, onEdit, onDelete }) => {
  const [showActions, setShowActions] = useState(false);

  return (
    <tr className="border-b border-border hover:bg-secondary/30 transition-colors">
      <td className="p-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
            <User className="w-5 h-5 text-primary" />
          </div>
          <div>
            <p className="font-medium text-white">{member.name}</p>
            <p className="text-xs text-muted-foreground">{member.email}</p>
          </div>
        </div>
      </td>
      <td className="p-4">
        <RoleBadge role={member.role} />
      </td>
      <td className="p-4">
        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
          member.status === 'active' ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'
        }`}>
          {member.status === 'active' ? 'Active' : 'Pending'}
        </span>
      </td>
      <td className="p-4 text-muted-foreground text-sm">
        {member.assignedEvents.length === 0 
          ? <span className="text-primary">All Events</span>
          : `${member.assignedEvents.length} events`
        }
      </td>
      <td className="p-4">
        <div className="relative">
          <button 
            onClick={() => setShowActions(!showActions)}
            className="p-2 hover:bg-secondary rounded-lg"
          >
            <ChevronDown className="w-4 h-4 text-muted-foreground" />
          </button>
          
          {showActions && (
            <>
              <div className="fixed inset-0 z-30" onClick={() => setShowActions(false)} />
              <div className="absolute right-0 top-full mt-1 w-36 bg-card border border-border rounded-lg shadow-xl overflow-hidden z-50">
                <button 
                  className="w-full px-4 py-2.5 text-left text-sm text-white hover:bg-secondary transition-colors flex items-center gap-2"
                  onClick={() => { onEdit(member); setShowActions(false); }}
                >
                  <Edit className="w-4 h-4" />
                  Edit Role
                </button>
                <button 
                  className="w-full px-4 py-2.5 text-left text-sm text-destructive hover:bg-secondary transition-colors flex items-center gap-2"
                  onClick={() => { onDelete(member); setShowActions(false); }}
                >
                  <Trash2 className="w-4 h-4" />
                  Remove
                </button>
              </div>
            </>
          )}
        </div>
      </td>
    </tr>
  );
};

export function AdminSettings() {
  const [activeTab, setActiveTab] = useState('team');
  const [teamMembers, setTeamMembers] = useState(initialTeamMembers);
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);

  const handleInviteUser = (userData) => {
    const newMember = {
      id: `tm-${Date.now()}`,
      name: userData.name,
      email: userData.email,
      role: userData.role,
      status: 'pending',
      assignedEvents: ['administrator', 'super_administrator'].includes(userData.role) ? [] : []
    };
    setTeamMembers([...teamMembers, newMember]);
    toast.success(`Invitation sent to ${userData.email}`);
  };

  const handleEditMember = (member) => {
    toast.info(`Edit ${member.name}'s role`);
  };

  const handleDeleteMember = (member) => {
    setTeamMembers(teamMembers.filter(m => m.id !== member.id));
    toast.success(`${member.name} has been removed`);
  };

  return (
    <AdminLayout>
      <div className="p-4 lg:p-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-xl lg:text-2xl xl:text-3xl font-bold text-white flex items-center gap-2">
              <Settings className="w-6 h-6 lg:w-8 lg:h-8 text-primary" />
              Settings
            </h1>
            <p className="text-muted-foreground mt-1 text-sm">Manage team access and platform settings</p>
          </div>
          <Button 
            className="bg-primary text-primary-foreground hover:bg-primary/90"
            onClick={() => setIsInviteModalOpen(true)}
          >
            <UserPlus className="w-4 h-4 mr-2" />
            Invite Team Member
          </Button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-border mb-6">
          <button
            onClick={() => setActiveTab('team')}
            className={`px-4 py-3 text-sm font-medium transition-colors ${
              activeTab === 'team' ? 'text-primary border-b-2 border-primary' : 'text-muted-foreground hover:text-white'
            }`}
          >
            <Users className="w-4 h-4 inline-block mr-2" />
            Team Members
          </button>
          <button
            onClick={() => setActiveTab('roles')}
            className={`px-4 py-3 text-sm font-medium transition-colors ${
              activeTab === 'roles' ? 'text-primary border-b-2 border-primary' : 'text-muted-foreground hover:text-white'
            }`}
          >
            <Shield className="w-4 h-4 inline-block mr-2" />
            Role Permissions
          </button>
        </div>

        {activeTab === 'team' && (
          <>
            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              <Card className="bg-card border-border p-4">
                <p className="text-xs text-muted-foreground">Total Members</p>
                <p className="text-2xl font-bold text-white">{teamMembers.length}</p>
              </Card>
              <Card className="bg-card border-border p-4">
                <p className="text-xs text-muted-foreground">Active</p>
                <p className="text-2xl font-bold text-green-400">{teamMembers.filter(m => m.status === 'active').length}</p>
              </Card>
              <Card className="bg-card border-border p-4">
                <p className="text-xs text-muted-foreground">Pending Invites</p>
                <p className="text-2xl font-bold text-yellow-400">{teamMembers.filter(m => m.status === 'pending').length}</p>
              </Card>
              <Card className="bg-card border-border p-4">
                <p className="text-xs text-muted-foreground">Admins</p>
                <p className="text-2xl font-bold text-primary">{teamMembers.filter(m => ['administrator', 'super_administrator'].includes(m.role)).length}</p>
              </Card>
            </div>

            {/* Team Members Table */}
            <Card className="bg-card border-border overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-border bg-secondary/30">
                      <th className="text-left p-4 text-xs uppercase tracking-wider text-muted-foreground font-medium">Member</th>
                      <th className="text-left p-4 text-xs uppercase tracking-wider text-muted-foreground font-medium">Role</th>
                      <th className="text-left p-4 text-xs uppercase tracking-wider text-muted-foreground font-medium">Status</th>
                      <th className="text-left p-4 text-xs uppercase tracking-wider text-muted-foreground font-medium">Access</th>
                      <th className="p-4"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {teamMembers.map(member => (
                      <TeamMemberRow 
                        key={member.id} 
                        member={member} 
                        onEdit={handleEditMember}
                        onDelete={handleDeleteMember}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          </>
        )}

        {activeTab === 'roles' && (
          <div className="space-y-6">
            <p className="text-muted-foreground">
              Role permissions define what actions team members can perform on the platform. 
              <span className="text-primary"> Artists & Stream Engineers</span> only have access to their assigned events, 
              while <span className="text-primary">Administrators</span> have full platform access.
            </p>

            <div className="grid gap-4">
              {Object.values(ROLES).map(role => (
                <Card key={role.id} className="bg-card border-border p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-lg ${role.color}/20 flex items-center justify-center`}>
                        <Shield className={`w-5 h-5 ${role.color.replace('bg-', 'text-')}`} />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-white">{role.name}</h3>
                        <p className="text-sm text-muted-foreground">{role.description}</p>
                      </div>
                    </div>
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${role.color}/20 ${role.color.replace('bg-', 'text-')}`}>
                      {teamMembers.filter(m => m.role === role.id).length} members
                    </span>
                  </div>

                  <div className="border-t border-border pt-4">
                    <h4 className="text-sm font-medium text-white mb-3">Permissions</h4>
                    <div className="flex flex-wrap gap-2">
                      {role.permissions.map(perm => (
                        <span key={perm} className="px-2 py-1 bg-secondary/50 rounded text-xs text-muted-foreground">
                          {perm.replace(/_/g, ' ')}
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* Access Rules Note */}
                  {['artist', 'stream_engineer', 'viewer', 'editor'].includes(role.id) && (
                    <div className="mt-4 p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
                      <p className="text-xs text-yellow-400">
                        <strong>Restricted Access:</strong> This role can only access events they are explicitly assigned to.
                      </p>
                    </div>
                  )}

                  {['administrator', 'super_administrator'].includes(role.id) && (
                    <div className="mt-4 p-3 bg-green-500/10 border border-green-500/30 rounded-lg">
                      <p className="text-xs text-green-400">
                        <strong>Full Access:</strong> This role has access to all events and platform features.
                      </p>
                    </div>
                  )}
                </Card>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Invite Modal */}
      <InviteUserModal
        isOpen={isInviteModalOpen}
        onClose={() => setIsInviteModalOpen(false)}
        onInvite={handleInviteUser}
      />
    </AdminLayout>
  );
}

export default AdminSettings;
